import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Decimal } from 'decimal.js'
import { TradeOrderSideEnum } from '@sparling/financial-common'
import { balancesSelector } from '../store/selectors'

interface BalanceValidatorProps {
  side: TradeOrderSideEnum
  baseCurrency: string
  quoteCurrency: string
  amount: number
  quantity: number
  commissionAmount: number
  isCommissionInQuote: boolean
}

export const useBalanceValidator = ({
  side,
  baseCurrency,
  quoteCurrency,
  amount,
  quantity,
  commissionAmount,
  isCommissionInQuote,
}: BalanceValidatorProps): { isEnoughBalance: boolean; reason?: string } => {
  const balances = useSelector(balancesSelector)

  const balanceMap = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    return balances?.data?.reduce<Record<string, Decimal>>((acc, { asset, amount }) => {
      acc[asset] = new Decimal(amount || 0)
      return acc
    }, {})
  }, [balances])

  return useMemo(() => {
    if (!baseCurrency || !quoteCurrency) return { isEnoughBalance: true }

    if (side === TradeOrderSideEnum.BUY) {
      const totalCost = new Decimal(amount).plus(isCommissionInQuote ? commissionAmount : 0)
      const quoteBalance = balanceMap[quoteCurrency] || new Decimal(0)

      if (quoteBalance.greaterThanOrEqualTo(totalCost)) {
        return { isEnoughBalance: true }
      }

      return {
        isEnoughBalance: false,
        reason: `Insufficient ${quoteCurrency} balance. Required: ${totalCost.toFixed(2)}.`,
      }
    }

    if (side === TradeOrderSideEnum.SELL) {
      const totalRequired = new Decimal(quantity).plus(!isCommissionInQuote ? commissionAmount : 0)
      const baseBalance = balanceMap[baseCurrency] || new Decimal(0)

      if (baseBalance.greaterThanOrEqualTo(totalRequired)) {
        return { isEnoughBalance: true }
      }

      return {
        isEnoughBalance: false,
        reason: `Insufficient ${baseCurrency} balance. Required: ${totalRequired.toFixed(8)}.`,
      }
    }

    return { isEnoughBalance: true }
  }, [side, baseCurrency, quoteCurrency, amount, quantity, commissionAmount, isCommissionInQuote, balanceMap])
}
